import { Box, Typography, Button, Modal, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Switch } from "@mui/material";
import { useEffect, useState } from "react";
import { Toast } from 'primereact/toast';
import { useRef } from "react";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CustomAxios from "../../utils/CustomAxios";
import useStatusToggle from "../../hooks/useStatusToggle";
import StatusSwitch from "../../sub_components/status_switch";

export function BOSettings() {
    const [businessOperations, setBusinessOperations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        operation_name: '',
        is_active: true
    });
    const [selectedId, setSelectedId] = useState(null);
    const statusToggle = useStatusToggle();
    const toast = useRef(null);

    useEffect(() => {
        fetchBusinessOperations();
    }, []);

    const fetchBusinessOperations = async () => {
        try {
            setLoading(true);
            const response = await CustomAxios.get('business-operations/');
            setBusinessOperations(response.data);
        } catch (error) {
            showToast('error', 'Error', 'Failed to fetch business operations');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditMode(true);
            setSelectedId(item.id);
            setFormData({
                operation_name: item.operation_name,
                is_active: item.is_active
            });
        } else {
            setEditMode(false);
            setSelectedId(null);
            setFormData({
                operation_name: '',
                is_active: true
            });
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditMode(false);
        setSelectedId(null);
        setFormData({
            operation_name: '',
            is_active: true
        });
    };

    const handleSubmit = async () => {
        if (!formData.operation_name.trim()) {
            showToast('error', 'Validation Error', 'Operation name is required');
            return;
        }

        try {
            setLoading(true);
            if (editMode) {
                await CustomAxios.put(`business-operations/${selectedId}/`, formData);
                showToast('success', 'Success', 'Business operation updated successfully');
            } else {
                await CustomAxios.post('business-operations/create/', formData);
                showToast('success', 'Success', 'Business operation created successfully');
            }
            handleCloseModal();
            fetchBusinessOperations();
        } catch (error) {
            showToast('error', 'Error', error.response?.data?.error || 'Operation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this business operation?')) {
            return;
        }

        try {
            setLoading(true);
            await CustomAxios.delete(`business-operations/${id}/`);
            showToast('success', 'Success', 'Business operation deleted successfully');
            fetchBusinessOperations();
        } catch (error) {
            showToast('error', 'Error', error.response?.data?.error || 'Delete failed');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (item) => {
        const next = !item.is_active;
        await statusToggle.run(item.id, next, async () => {
            try {
                await CustomAxios.put(`business-operations/${item.id}/`, {
                    ...item,
                    is_active: next
                });
            } catch (error) {
                showToast('error', 'Error', 'Failed to update status');
                throw error;
            }
            showToast('success', 'Success', 'Status updated successfully');
            await fetchBusinessOperations();
        });
    };

    const showToast = (severity, summary, detail) => {
        if (toast.current) {
            toast.current.show({ severity, summary, detail, life: 3000 });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">BO Settings</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal()}
                >
                    Add Business Operation
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Operation Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {businessOperations.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.operation_name}</TableCell>
                                <TableCell>
                                    <StatusSwitch
                                        checked={statusToggle.displayed(item.id, item.is_active)}
                                        pending={statusToggle.isPending(item.id)}
                                        onChange={() => handleToggleActive(item)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleOpenModal(item)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(item.id)}>
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal open={modalOpen} onClose={handleCloseModal}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {editMode ? 'Edit' : 'Add'} Business Operation
                    </Typography>
                    <TextField
                        fullWidth
                        label="Operation Name"
                        name="operation_name"
                        value={formData.operation_name}
                        onChange={(e) => setFormData({ ...formData, operation_name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ mr: 2 }}>Active Status:</Typography>
                        <Switch
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal}>Cancel</Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                            {editMode ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

